/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
  SIZE,
} from "baseui/modal";
import { TOP_ITEM_ZINDEX } from "../Utils/SiteProps";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

export interface DexterModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;

  /** Header text. */
  title: string | React.JSX.Element;

  /** Modal body. Each modal supplies its own content here. */
  children: React.ReactNode;

  /** Label for the primary (confirm) button, e.g. "Upload" or "Confirm". */
  confirmLabel: string;

  /** Called when the confirm button is clicked. */
  onConfirm: () => void;

  /** Called on Cancel, the close (X), or a backdrop click. */
  onCancel: () => void;

  /** Disables the confirm button when true. */
  confirmDisabled?: boolean;

  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;

  /**Custom button like a back button set to the far left of the modal footer */
  footerStart?: React.ReactNode;

  /**Custom width for the modal */
  customWidth?: number;
}

/**
 * The shared Dexter modal shell: a titled dialog with a Cancel button and a
 * primary Confirm button. The body and the confirm label/behavior are injected,
 * so specific modals reuse the same chrome and only differ in their content and
 * what "confirm" does.
 */
export const DexterModal = ({
  isOpen,
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  cancelLabel = "Cancel",
  footerStart,
  customWidth,
}: DexterModalProps): JSX.Element => {
  const { currentTheme } = useDexterTheme();

  return (
    <Modal
      size={customWidth ? customWidth : SIZE.default}
      overrides={{
        Root: { style: { zIndex: TOP_ITEM_ZINDEX } },
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
          },
        },
      }}
      isOpen={isOpen}
      onClose={onCancel}
    >
      <ModalHeader
        style={{
          backgroundColor: currentTheme.colors.pageBackgroundColor,
          color: currentTheme.colors.defaultTextColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.large,
        }}
      >
        {title}
      </ModalHeader>
      <ModalBody>{children}</ModalBody>
      <ModalFooter
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {footerStart && (
          <div
            style={{
              marginRight: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            {footerStart}
          </div>
        )}
        <ModalButton
          kind="tertiary"
          onClick={onCancel}
          overrides={{
            BaseButton: {
              style: {
                fontFamily: currentTheme.fonts.primary,
                fontSize: currentTheme.fontSizes.medium,
                backgroundColor: "transparent",
                color: currentTheme.colors.text,
              },
            },
          }}
        >
          {cancelLabel}
        </ModalButton>
        <ModalButton
          kind="primary"
          disabled={confirmDisabled}
          onClick={onConfirm}
          overrides={{
            BaseButton: {
              style: {
                fontFamily: currentTheme.fonts.primary,
                fontSize: currentTheme.fontSizes.medium,
                backgroundColor: confirmDisabled
                  ? currentTheme.colors.backgroundTertiary
                  : currentTheme.colors.primaryButtonColor,
                color: confirmDisabled
                  ? currentTheme.colors.buttonDisabledColor
                  : currentTheme.colors.primaryElementFontColor,
              },
            },
          }}
        >
          {confirmLabel}
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};

export default DexterModal;
